import { useState } from "react";
import { http } from "@/lib/api";
import { X, Sparkle } from "@phosphor-icons/react";

const SUBJECTS = [
  "tree", "river", "stream", "lake", "pond", "wetland",
  "beach", "shore", "coast", "garden", "farm", "field",
  "forest", "meadow", "mountain", "hill", "glacier",
  "backyard", "park", "other",
];
const SCHEDULES = ["weekly", "monthly", "seasonally", "yearly"];

export default function AdoptAPlaceModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    subject_type: "tree",
    name: "",
    description: "",
    schedule: "seasonally",
    country: "",
    state: "",
    city: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Please give this place a name."); return; }
    setSaving(true); setError("");
    try {
      const { data } = await http.post(`/adopt-a-place`, {
        subject_type: form.subject_type,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        schedule: form.schedule,
        location: {
          country: form.country.trim() || undefined,
          state: form.state.trim() || undefined,
          city: form.city.trim() || undefined,
        },
      });
      onCreated?.(data?.place);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save adoption. Try again.");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="adopt-modal-backdrop"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full md:max-w-lg bg-[#F5EBDC] rounded-t-3xl md:rounded-3xl overflow-hidden border border-black/10 shadow-2xl"
        data-testid="adopt-modal"
      >
        <div className="flex items-start justify-between p-5 md:p-6 border-b border-black/10">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              / Adopt a place
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight">
              What will you watch over time?
            </h2>
          </div>
          <button type="button" onClick={onClose} data-testid="adopt-modal-close" className="p-1 rounded-full hover:bg-black/5">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-5 md:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">What kind of place?</label>
            <select
              value={form.subject_type}
              onChange={(e) => setForm({ ...form, subject_type: e.target.value })}
              data-testid="adopt-modal-subject"
              className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[15px]"
            >
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">Name it</label>
            <input
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. The old oak at Meadow Lane"
              required maxLength={200}
              data-testid="adopt-modal-name"
              className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[15px]"
            />
          </div>

          <div>
            <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">Why this place?</label>
            <textarea
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Where will you photograph it from? What are you hoping to notice over time?"
              rows={3} maxLength={4000}
              data-testid="adopt-modal-desc"
              className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[15px]"
            />
          </div>

          <div>
            <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">How often will you return?</label>
            <div className="mt-1 grid grid-cols-4 gap-2">
              {SCHEDULES.map((s) => (
                <button
                  type="button" key={s}
                  onClick={() => setForm({ ...form, schedule: s })}
                  data-testid={`adopt-modal-schedule-${s}`}
                  className={`rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase border transition ${
                    form.schedule === s
                      ? "bg-[#241209] text-[#F5EBDC] border-[#241209]"
                      : "bg-white border-black/15 hover:border-black/40"
                  }`}
                >{s}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">Country</label>
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="adopt-modal-country"
                className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[14px]" />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">State / Region</label>
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                data-testid="adopt-modal-state"
                className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[14px]" />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">City</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="adopt-modal-city"
                className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-[14px]" />
            </div>
          </div>

          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="adopt-modal-error">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 md:p-6 border-t border-black/10 bg-white/60">
          <button type="button" onClick={onClose} className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]/70 hover:text-[#241209]">
            Cancel
          </button>
          <button
            type="submit" disabled={saving}
            data-testid="adopt-modal-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-60"
          >
            <Sparkle size={13} weight="fill" /> {saving ? "Saving…" : "Adopt this place"}
          </button>
        </div>
      </form>
    </div>
  );
}
